import * as THREE from 'three';

import {
  BrightnessContrastEffect,
  EffectComposer,
  EffectPass,
  RenderPass,
  SMAAEffect,
} from 'postprocessing';

import GUI from 'lil-gui';
import { lerp } from 'three/src/math/MathUtils.js';
import { detectHardwareTier, type HardwareTier } from './hardwareDetection';

export interface ThreeBase {
  time: { clock: THREE.Clock; previousTime: number };
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  sizes: { width: number; height: number };
  camera: THREE.PerspectiveCamera;
  cameraTargetPosition: THREE.Vector3;
  cameraTargetRotation: THREE.Vector3;
  cameraParallaxTargetRotation: THREE.Vector3;
  lastCameraPosition: THREE.Vector3;
  setParallaxEnabled: (state: boolean) => void;
  updateRenderer: (newSizes: { width: number; height: number }) => void;
  getWorldSizes: () => { height: number; width: number };
  updateCamera: (s: { width: number; height: number }) => void;
  updateSizes: () => void;
  addOnTick: (f: (dT: number, eT: number) => void) => void;
  startTick: () => void;
  stopTick: () => void;
  addRaycasterObject: (obj: THREE.Mesh) => void;
  addVisibilityObject: (
    obj: THREE.Object3D,
    offset: number,
    range?: { front: number; back: number },
    visibleAtStart?: boolean,
  ) => void;
  updateVisibleObjects: (newCameraPosition: THREE.Vector3) => void;
  textureLoader: THREE.TextureLoader;
  resetCameraPosition: () => void;
}

export default function useThreeBase(
  containerElement: HTMLElement,
  canvas: HTMLCanvasElement,
  distance: number,
  backgroundColor: string = '#ffffff',
  resolutionScale = 1,
  debugEnabled = false,
  textColor = '#ffffff',
  parallaxEnabled = false,
  parallaxStrength = 1,
  maxFPS = 60,
): ThreeBase {
  const sizes = {
    width: document.documentElement.clientWidth,
    height: containerElement.clientHeight,
  };

  const { isVs } = useViewportSize();

  // Hardware detection
  const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
  const hardwareTier = detectHardwareTier(gl);

  // Adjust settings based on hardware
  const adjustedResolution = adjustResolutionForHardware(
    resolutionScale,
    hardwareTier,
  );
  const adjustedMaxFPS = adjustFPSForHardware(maxFPS, hardwareTier);
  const time = {
    clock: new THREE.Clock(),
    previousTime: 0,
  };

  let frameInterval = 1000 / adjustedMaxFPS;
  let lastFrameTime = 0;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(backgroundColor);
  scene.fog = new THREE.Fog('#E6E4E0', 10, 100);

  // raycaster
  const raycaster = new THREE.Raycaster();
  raycaster.far = 20;
  const pointer = new THREE.Vector2();
  const rayscasterObjects: THREE.Mesh[] = [];

  interface VisibilityObject {
    object: THREE.Object3D;
    offset: number;
    range: {
      front: number;
      back: number;
    };
  }

  // visibility
  const visibilityObserver: {
    objects: VisibilityObject[];
  } = {
    objects: [],
  };

  //
  // Renderer
  //
  const renderer = new THREE.WebGLRenderer({
    alpha: false,
    canvas,
    precision: 'lowp',
    powerPreference: 'high-performance',
    preserveDrawingBuffer: false,
    stencil: false,
    antialias: false, // We're using SMAA post-processing instead
  });
  if (typeof window !== 'undefined') {
    renderer.setPixelRatio(window.devicePixelRatio);
  }
  renderer.setSize(
    sizes.width * adjustedResolution,
    sizes.height * adjustedResolution,
    false,
  );
  if (typeof window !== 'undefined') {
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }
  renderer.outputColorSpace = THREE.DisplayP3ColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;

  //
  // Camera
  //

  const camera = new THREE.PerspectiveCamera(
    distance,
    sizes.width / sizes.height,
    0.1,
    isVs.value ? 20 : 100,
  );
  camera.rotation.x = -0.01;
  camera.updateProjectionMatrix();

  const cameraTargetPosition = new THREE.Vector3(0, 1.35, 7);
  const cameraTargetRotation = new THREE.Vector3(0, 0, 0);
  const cameraParallaxTargetRotation = new THREE.Vector3(0, 0, 0);
  const lastCameraPosition = new THREE.Vector3(0, 1.35, 7);

  function updateFocalLength() {
    if (typeof window !== 'undefined' && window.innerWidth < 900) {
      camera.setFocalLength(17);
    } else {
      camera.setFocalLength(21);
    }
  }

  updateFocalLength();

  //
  // Effect composer / Post Processing
  //
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  // Post Processing: Hue and Saturation
  // const hueSaturationEffect = new HueSaturationEffect({
  //   blendFunction: THREE.NormalBlending,
  //   hue: 0,
  //   saturation: 0,
  // });
  // composer.addPass(new EffectPass(camera, hueSaturationEffect));
  // Post Processing: Brightness and Contrast
  const brightnessContrastEffect = new BrightnessContrastEffect({
    blendFunction: THREE.NormalBlending,
    brightness: 0,
    contrast: 0.16,
  });
  composer.addPass(new EffectPass(camera, brightnessContrastEffect));
  // Post Processing: Antialiasing
  const smaaEffect = new SMAAEffect();
  if (hardwareTier !== 'low') {
    composer.addPass(new EffectPass(camera, smaaEffect));
  }

  //
  // GUI
  //

  const guiObj = {
    hue: 0,
    saturation: 0,
    brightness: 0,
    contrast: 0.16,
    parallaxEnabled: parallaxEnabled ?? false,
    parallaxXSensitivity: 5 * parallaxStrength,
    parallaxYSensitivity: 0 * parallaxStrength,
    textColor: textColor ?? '#ffffff',
    resolution: adjustedResolution,
    maxFPS: adjustedMaxFPS,
    hardwareTier,
  };

  const setParallaxEnabled = (state: boolean) => {
    guiObj.parallaxEnabled = state;
    if (!state) {
      cameraTargetRotation.x = 0;
      cameraTargetRotation.y = 0;
    }
  };

  const gui = new GUI();
  gui.close();
  // gui.add(guiObj, 'hue', -1, 1).onChange((value: number) => {
  //   hueSaturationEffect.hue = value;
  // });
  // gui.add(guiObj, 'saturation', -1, 1).onChange((value: number) => {
  //   hueSaturationEffect.saturation = value;
  // });
  gui.add(guiObj, 'brightness', -1, 1).onChange((value: number) => {
    brightnessContrastEffect.brightness = value;
  });
  gui.add(guiObj, 'contrast', -1, 1).onChange((value: number) => {
    brightnessContrastEffect.contrast = value;
  });
  gui.add(guiObj, 'parallaxEnabled').onChange(setParallaxEnabled);
  gui.add(guiObj, 'parallaxXSensitivity', 0, 180);
  gui.add(guiObj, 'parallaxYSensitivity', 0, 10);

  gui.addColor(guiObj, 'textColor').onChange((value: string) => {
    const groups = scene.children.filter((child) => child.type === 'Group');
    let texts: THREE.Mesh[] = [];
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];

      texts = group.children.filter(
        // @ts-ignore
        (child) => child.geometry.type === 'TextGeometry',
      ) as THREE.Mesh[];

      for (let j = 0; j < texts.length; j++) {
        const text = texts[j];
        (text.material as THREE.MeshBasicMaterial).color = new THREE.Color(
          value,
        );
      }
    }
  });

  gui.add(guiObj, 'resolution', 0.2, 4).onChange((value: number) => {
    resolutionScale = value;
    updateRenderer(sizes);
  });

  gui.add(guiObj, 'maxFPS', 1, 120).onChange((value: number) => {
    frameInterval = 1000 / value;
  });

  // Add hardware info to GUI
  gui.add(guiObj, 'hardwareTier').name('Hardware Tier').listen();

  gui.show(debugEnabled);

  const runningOnTick: ((dT: number, eT: number) => void)[] = [];

  let tickRequest: number | null;

  const textureLoader = new THREE.TextureLoader();

  let isResizing = false;
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateSizes);
  }

  return {
    time,
    scene,
    renderer,
    sizes,
    camera,
    cameraTargetPosition,
    cameraTargetRotation,
    cameraParallaxTargetRotation,
    lastCameraPosition,
    setParallaxEnabled,
    textureLoader,
    updateRenderer,
    getWorldSizes,
    updateCamera,
    updateSizes,
    addOnTick,
    startTick,
    stopTick,
    addRaycasterObject,
    addVisibilityObject,
    updateVisibleObjects,
    resetCameraPosition,
  };

  function startTick() {
    if (!tickRequest) {
      tick();

      if (typeof window !== 'undefined') {
        window.addEventListener('click', onClick);
        window.addEventListener('pointermove', onPointerMove);
      }
    }
  }

  function stopTick() {
    if (typeof tickRequest === 'number') {
      if (typeof window !== 'undefined') {
        window.cancelAnimationFrame(tickRequest);
        tickRequest = null;

        window.removeEventListener('click', onClick);
        window.removeEventListener('pointermove', onPointerMove);
      }
    }
  }

  function onClick(event: MouseEvent) {
    let w = 1500;
    let h = 900;
    if (typeof window !== 'undefined') {
      w = window.innerWidth;
      h = window.innerHeight;
    }
    const click = new THREE.Vector2(
      (event.clientX / w) * 2 - 1,
      -(event.clientY / h) * 2 + 1,
    );

    raycaster.setFromCamera(click, camera);
    const pointerIntersects = raycaster.intersectObjects(rayscasterObjects);

    for (let i = 0; i < pointerIntersects.length; i++) {
      const intersect = pointerIntersects[i];

      if (intersect.distance < 15 && 'onclick' in intersect.object.userData) {
        intersect.object.userData.onclick(true);
      }
    }
  }

  function onPointerMove(event: PointerEvent) {
    let w = 1500;
    let h = 900;
    if (typeof window !== 'undefined') {
      w = window.innerWidth;
      h = window.innerHeight;
    }
    pointer.x = (event.clientX / w) * 2 - 1;
    pointer.y = -(event.clientY / h) * 2 + 1;
  }

  function addOnTick(f: (dT: number, eT: number) => void) {
    runningOnTick.push(f);
  }

  function updateSizes() {
    if (isResizing) return;
    isResizing = true;
    setTimeout(() => {
      sizes.width = containerElement.clientWidth;
      sizes.height = containerElement.clientHeight;
      updateCamera(sizes);
      updateRenderer(sizes);
      isResizing = false;
    }, 500);
  }

  function tick() {
    const currentTime = performance.now();
    const elapsedTime = time.clock.getElapsedTime();
    const deltaTime = elapsedTime - time.previousTime;
    time.previousTime = elapsedTime;

    // Skip frame if not enough time has passed
    if (currentTime - lastFrameTime < frameInterval) {
      if (typeof window !== 'undefined') {
        tickRequest = window.requestAnimationFrame(tick);
      }
      return;
    }
    lastFrameTime = currentTime;

    for (let i = 0; i < runningOnTick.length; i++) {
      runningOnTick[i](deltaTime, elapsedTime);
    }

    camera.position.lerp(cameraTargetPosition, 0.2);
    composer.render();

    if (typeof window !== 'undefined') {
      tickRequest = window.requestAnimationFrame(tick);
    }

    raycaster.setFromCamera(pointer, camera);
    const intersects = raycaster.intersectObjects(rayscasterObjects);

    const intersectedObjects = new Set();

    for (let i = 0; i < intersects.length; i++) {
      const intersect = intersects[i];
      const obj = intersect.object as THREE.Mesh;
      intersectedObjects.add(obj);
      if (
        obj.name !== 'frame' &&
        !obj.userData.isHovered &&
        document.documentElement.getAttribute('data-experience') === 'running'
      ) {
        // @ts-ignore
        (obj.material as THREE.Material).color = new THREE.Color(0xcecece);
        obj.userData.isHoveredTimeline?.play(true);
        obj.userData.isHovered = true;
        document.documentElement.style.cursor = 'pointer';
      }
    }

    for (let i = 0; i < rayscasterObjects.length; i++) {
      const obj = rayscasterObjects[i];
      if (
        obj.name !== 'frame' &&
        obj.userData.isHovered &&
        !intersectedObjects.has(obj)
      ) {
        // @ts-ignore
        (obj.material as THREE.Material).color = new THREE.Color(0xffffff);
        obj.userData.isHoveredTimeline?.play(false);
        obj.userData.isHovered = false;
        document.documentElement.style.cursor = 'default';
      }
    }

    // todo if Dimitri asks for camera parallax, add these lines of codd (but try to talk him out of it)
    const parallaxX = -pointer.x * guiObj.parallaxXSensitivity;
    const parallaxY = pointer.y * guiObj.parallaxYSensitivity;

    if (guiObj.parallaxEnabled) {
      cameraParallaxTargetRotation.x = Math.PI * parallaxY * 0.01;
      cameraParallaxTargetRotation.y = Math.PI * parallaxX * 0.005;
    }
    camera.rotation.x = lerp(
      camera.rotation.x,
      cameraTargetRotation.x + cameraParallaxTargetRotation.x,
      0.1,
    );
    camera.rotation.y = lerp(
      camera.rotation.y,
      cameraTargetRotation.y + cameraParallaxTargetRotation.y,
      0.1,
    );
  }

  function addRaycasterObject(obj: THREE.Mesh) {
    rayscasterObjects.push(obj);
  }

  function addVisibilityObject(
    object: THREE.Object3D,
    offset: number,
    range: { front: number; back: number } = { front: 1, back: -4 },
    visibleAtStart = false,
  ) {
    visibilityObserver.objects.push({ object, offset, range });
    object.userData.isVisible = visibleAtStart;
    object.visible = visibleAtStart;
  }

  function updateVisibleObjects(newCameraPosition: THREE.Vector3) {
    for (let i = 0; i < visibilityObserver.objects.length; i++) {
      const visObj = visibilityObserver.objects[i];
      const obj = visObj.object;
      const oldVisibility = obj.userData.isVisible;
      const position = obj.position.z + visObj.offset;
      const range = {
        front: newCameraPosition.z + visObj.range.front,
        back: newCameraPosition.z + visObj.range.back,
      };
      obj.userData.isVisible = position > range.back && position < range.front;

      const hasChangedVisibility = obj.userData.isVisible !== oldVisibility;

      if (hasChangedVisibility) {
        if (obj.userData.isVisible) {
          obj.visible = obj.userData.isVisible;
        }

        if (obj.userData.toggleVisibleTimeline?.play) {
          obj.userData.toggleVisibleTimeline.play(obj.userData.isVisible);
        }

        if (!obj.userData.isVisible) {
          setTimeout(() => {
            if (!obj.userData.isVisible) {
              obj.visible = obj.userData.isVisible;
            }
          }, 500);
        }
      }
    }
  }

  function updateRenderer(newSizes: { width: number; height: number }) {
    if (isVs.value) {
      renderer.setSize(
        newSizes.width * resolutionScale * 0.5,
        newSizes.height * resolutionScale * 0.5,
        false,
      );
    } else {
      renderer.setSize(
        newSizes.width * resolutionScale,
        newSizes.height * resolutionScale,
        false,
      );
    }
    if (typeof window !== 'undefined') {
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }
  }

  function getWorldSizes() {
    const vFOV = THREE.MathUtils.degToRad(camera.fov);
    const height = 2 * Math.tan(vFOV / 2) * distance;
    const width = height * camera.aspect;

    return {
      height,
      width,
    };
  }

  function updateCamera(s: { width: number; height: number }) {
    camera.aspect = s.width / s.height;
    camera.far = isVs.value ? 20 : 100;
    updateFocalLength();
    camera.updateProjectionMatrix();
  }

  function resetCameraPosition() {
    cameraTargetPosition.set(
      lastCameraPosition.x,
      lastCameraPosition.y,
      lastCameraPosition.z,
    );
    camera.rotation.x = -0.01;
  }
}

function adjustResolutionForHardware(
  resolution: number,
  tier: HardwareTier,
): number {
  switch (tier) {
    case 'high':
      return resolution;
    case 'medium':
      return Math.min(resolution, 1);
    case 'low':
      return Math.min(resolution, 0.7);
    default:
      return resolution;
  }
}

function adjustFPSForHardware(fps: number, tier: HardwareTier): number {
  switch (tier) {
    case 'high':
      return fps;
    case 'medium':
      return Math.min(fps, 60);
    case 'low':
      return Math.min(fps, 30);
    default:
      return fps;
  }
}
